import axios from "axios";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { setLoader } from "../../Store/Reducers/UserReducer";
import { useContextMenu } from "../../Layout Component/ContextMenu";
import Folliculogram, { readFolliculogramVersions } from "./Folliculogram";
import DicomViewer from "./DicomViewer";
import { appointmentsIn, cycleSpan, follicleTotals, keyDatesOf, latestDays, printFolliculogram, printSchedule } from "./cyclePrint";

// A treatment cycle, opened from the overview or the treatment list, to LOOK
// at: the folliculogram and the digital images, nothing to edit. Laid out like
// the clinical Folliculogram & Imaging page: the treatment and its dates, the
// follicle counts, the key dates and visits as chips, then the grid. Print
// gives the folliculogram, or a medication schedule (drugs and appointments).
// (The older Details page, TreatmentOutcome, is still there from the context
// menu; it is where the patient records a pregnancy outcome.)

// A page that cannot draw says so, instead of blanking the whole portal.
class Guard extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <p className="ox-hint" role="alert">
          This could not be drawn ({String(this.state.error.message || this.state.error)}). Please go back and try again.
        </p>
      );
    }
    return this.props.children;
  }
}

// What to say when a folliculogram is on file but nothing could be read from it.
function Unreadable({ json }) {
  const text = typeof json === "string" ? json : JSON.stringify(json);
  return (
    <div className="ox-hint" role="status">
      <p>A folliculogram is saved for this treatment, but its layout was not recognised, so it cannot be drawn here yet.</p>
      <details>
        <summary>Show what was received</summary>
        <pre style={{ whiteSpace: "pre-wrap", maxHeight: 220, overflow: "auto" }}>{String(text).slice(0, 1500)}</pre>
      </details>
    </div>
  );
}

const dayText = (raw) => {
  if (!raw || String(raw).startsWith("0001-01-01")) return null;
  const m = moment(raw);
  return m.isValid() && m.year() > 1 ? m.format("DD-MMM-YYYY") : null;
};

export default function TreatmentCycleView() {
  const url = process.env.REACT_APP_OXAR_API_URL;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { show, menu } = useContextMenu();

  const id = state?.id || localStorage.getItem("Tretmentid");
  const [tab, setTab] = useState(state?.tab === "images" ? "images" : "folliculogram");
  const [cycle, setCycle] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    let alive = true;
    (async () => {
      try {
        dispatch(setLoader(true));
        const res = await axios(`${url}api/Treatment/GetTreatmentCycleById?id=${id}`);
        if (!alive) return;
        setCycle(res.data?.content || null);
        // The images are found by the cycle's name.
        const name = state?.name || res.data?.content?.name || "";
        sessionStorage.setItem("Treatmentid", id);
        sessionStorage.setItem("TreatmentName", name);
        localStorage.setItem("Tretmentid", id);
        // The folder is named "<type> <start> - <patient> & <partner>", with "/"
        // written "-". Offered to the image loader beside the stored name.
        const alt = [];
        if (state?.type && state?.start && state?.couple) {
          alt.push(`${state.type.replace(/\s*\/\s*/g, "-")} ${state.start} - ${state.couple}`);
        }
        sessionStorage.setItem("TreatmentAltNames", JSON.stringify(alt));
      } catch {
        if (alive) setError("This treatment could not be loaded. Please go back and try again.");
      } finally {
        dispatch(setLoader(false));
      }
      try {
        const uid = localStorage.getItem("UserID");
        const a = await axios(`${url}api/AppointmentDosage/GetAppointments?contactId=${uid}`);
        if (alive) setAppointments(a.data?.content || []);
      } catch {
        // Visits and the printed schedule simply have no appointments.
      }
    })();
    return () => { alive = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = state?.title || cycle?.name || "Treatment";
  const type = state?.type || cycle?.bcrm_treatment_type_os_name;
  const start = state?.start || dayText(cycle?.bcrm_treatment_start_date || cycle?.treatmentStartDate);
  const couple = state?.couple || "";

  const { days, number, count } = useMemo(() => latestDays(cycle?.folliculagramJSON), [cycle]);
  const span = useMemo(() => cycleSpan(days), [days]);
  const totals = useMemo(() => follicleTotals(days), [days]);
  const keyDates = useMemo(() => keyDatesOf(cycle, span?.from), [cycle, span]);
  const visits = useMemo(() => appointmentsIn(appointments, span).slice(0, 12), [appointments, span]);
  const unreadable = cycle?.folliculagramJSON && readFolliculogramVersions(cycle.folliculagramJSON).length === 0;

  const printWith = (fn) => () => {
    if (!cycle) return;
    const ok = fn({ title: `${title}${couple ? ` · ${couple}` : ""}`, couple, type, start, cycle, appointments });
    if (!ok) toast.warn("Your browser blocked the print window. Please allow pop-ups for this site.");
  };
  const printFolli = printWith(printFolliculogram);
  const printSched = printWith(printSchedule);

  return (
    <div
      className="ox-cycle"
      onContextMenu={(e) => show(e, [
        { label: "Folliculogram", onClick: () => setTab("folliculogram") },
        { label: "Images", onClick: () => setTab("images") },
        { divider: true },
        { label: "Print folliculogram", onClick: printFolli },
        { label: "Print medication schedule", onClick: printSched },
        { divider: true },
        { label: "Details and outcome", onClick: () => navigate("/TreatmentOutcome", { state: { Tid: id } }) },
        { label: "Back to my treatments", onClick: () => navigate("/Treatments") },
      ])}
    >
      <div className="ox-folhead">
        <span className="ox-pillbox"><em>Treatment</em><strong>{title}{couple ? ` · ${couple}` : ""}</strong></span>
        {span && (
          <span className="ox-pillbox"><em>Dates</em><strong>{span.from.format("DD-MMM-YYYY")} {"–"} {span.to.format("DD-MMM-YYYY")}</strong><small>{span.count} days</small></span>
        )}
        {!span && start && <span className="ox-pillbox"><em>Start</em><strong>{start}</strong></span>}
        {number !== null && <span className="ox-pillbox"><em>Version</em><strong>{number}{count > 1 ? ` of ${count}` : ""}</strong><small>Current</small></span>}
        {span && (
          <>
            <span className="ox-count ox-count--r">Right {totals.right}</span>
            <span className="ox-count ox-count--l">Left {totals.left}</span>
          </>
        )}
        <span className="ox-folhead__go">
          <span className="ox-chip ox-chip--viewing">Read only</span>
          <button type="button" className="ox-btn" onClick={printFolli} disabled={!cycle}>Print folliculogram</button>
          <button type="button" className="ox-btn" onClick={printSched} disabled={!cycle}>Print medication schedule</button>
          <button type="button" className="ox-btn" onClick={() => navigate(-1)}>Back</button>
        </span>
      </div>

      {error && <p className="ox-hint" role="alert">{error}</p>}

      <div className="ox-tabs" role="tablist">
        {[["folliculogram", "Folliculogram"], ["images", "Images"]].map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key}
            className={`ox-tab${tab === key ? " is-active" : ""}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === "folliculogram" && (keyDates.length > 0 || visits.length > 0) && (
        <div className="ox-chips">
          {keyDates.length > 0 && (
            <div className="ox-chips__row">
              <b>Key dates</b>
              {keyDates.map((k) => (
                <span key={k.label} className="ox-keychip">
                  <strong>{k.label}</strong> {k.date.format("DD-MMM")}
                  {k.day !== null && <em>D{k.day}</em>}
                </span>
              ))}
            </div>
          )}
          {visits.length > 0 && (
            <div className="ox-chips__row">
              <b>Visits</b>
              {visits.map((v) => (
                <span key={`${v.id}-${v.startTime}`} className="ox-keychip ox-keychip--visit" title={`${v.subject || ""} · ${moment(v.startTime).format("ddd DD-MMM HH:mm")}`}>
                  <strong>{v.appointmentlibraryitemName || v.subject || "Appointment"}</strong> {moment(v.startTime).format("DD-MMM")}
                  {span && <em>D{moment(v.startTime).startOf("day").diff(span.from.clone().startOf("day"), "days") + 1}</em>}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="ox-section">
        <div className="ox-section__body">
          {tab === "folliculogram" ? (
            cycle ? (
              <Guard>
                {unreadable ? <Unreadable json={cycle.folliculagramJSON} /> : <Folliculogram json={cycle.folliculagramJSON} />}
              </Guard>
            ) : !error && <p className="ox-hint">Loading...</p>
          ) : (
            // Mounted once the cycle is known: the viewer finds the images by
            // the cycle name stored above.
            cycle ? <Guard><DicomViewer /></Guard> : !error && <p className="ox-hint">Loading...</p>
          )}
        </div>
      </div>
      {menu}
    </div>
  );
}
